/**
 * Asset Store - manages loaded sprite assets with dynamic loading
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Assets, Texture as PixiTexture, Rectangle } from 'pixi.js';

/** Atlas entry for a single sprite frame */
export interface AtlasEntry {
    image: string;
    frame: { x: number; y: number; w: number; h: number };
    pivot?: { x: number; y: number };
}

/** Full tile atlas */
export interface TileAtlas {
    tiles: Record<string, AtlasEntry>;
    spritesheets: string[];
}

/** Yield to the main thread to prevent freezing */
function yieldToMain(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
}

export const useAssetsStore = defineStore('assets', () => {
    // State - using 'any' for PixiJS textures due to complex internal types
    const atlas = ref<TileAtlas | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseTextures = ref<Map<string, any>>(new Map());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const frameTextures = ref<Map<string, any>>(new Map());
    const loading = ref(false);
    const error = ref<string | null>(null);
    const loadProgress = ref(0);
    const loadingStatus = ref<string>('');

    // Track which spritesheets are loaded or loading
    const loadedSpritesheets = ref<Set<string>>(new Set());
    const loadingSpritesheets = ref<Set<string>>(new Set());

    // Pre-built indexes for fast lookup
    // image path -> list of [key, entry] pairs
    const spritesheetIndex = ref<Map<string, Array<[string, AtlasEntry]>>>(new Map());
    // spriteId -> Set of image paths that contain frames for this sprite
    const spriteIdToImages = ref<Map<number, Set<string>>>(new Map());
    // "spriteId_frame" -> atlas key (for fast texture lookup)
    const spriteFrameToKey = ref<Map<string, string>>(new Map());
    // PAK name (lowercase) -> Set of sprite IDs in that PAK
    const pakToSpriteIds = ref<Map<string, Set<number>>>(new Map());

    // Computed
    const isLoaded = computed(() => atlas.value !== null && !loading.value);

    const availableTileSprites = computed(() => {
        return Array.from(spriteIdToImages.value.keys()).sort((a, b) => a - b);
    });

    // Actions

    /**
     * Load only the atlas metadata and build indexes incrementally
     */
    async function loadAtlas(): Promise<void> {
        if (loading.value || atlas.value) return;

        loading.value = true;
        error.value = null;
        loadingStatus.value = 'Fetching atlas...';

        try {
            const response = await fetch('assets/atlas.json');
            if (!response.ok) {
                throw new Error(`Failed to load atlas: ${response.statusText}`);
            }

            loadingStatus.value = 'Parsing atlas JSON...';
            await yieldToMain();

            const loadedAtlas: TileAtlas = await response.json();
            atlas.value = loadedAtlas;

            loadingStatus.value = 'Building indexes...';
            await yieldToMain();

            // Build indexes in chunks to avoid freezing
            const entries = Object.entries(loadedAtlas.tiles);
            const totalEntries = entries.length;
            const CHUNK_SIZE = 50000;

            const imgIndex = new Map<string, Array<[string, AtlasEntry]>>();
            const spriteIndex = new Map<number, Set<string>>();
            const frameIndex = new Map<string, string>();
            const pakIndex = new Map<string, Set<number>>();

            for (let i = 0; i < totalEntries; i += CHUNK_SIZE) {
                const chunk = entries.slice(i, i + CHUNK_SIZE);

                for (const [key, entry] of chunk) {
                    // Build spritesheet index
                    const list = imgIndex.get(entry.image);
                    if (list) {
                        list.push([key, entry]);
                    } else {
                        imgIndex.set(entry.image, [[key, entry]]);
                    }

                    // Extract PAK name, sprite ID and frame from key (pattern: pakname_spriteId_frame)
                    const match = key.match(/^(.+)_(\d+)_(\d+)$/);
                    if (match) {
                        const pakName = match[1].toLowerCase();
                        const spriteId = parseInt(match[2], 10);
                        const frame = parseInt(match[3], 10);

                        // Build sprite ID -> images index
                        const imgSet = spriteIndex.get(spriteId);
                        if (imgSet) {
                            imgSet.add(entry.image);
                        } else {
                            spriteIndex.set(spriteId, new Set([entry.image]));
                        }

                        // Build sprite_frame -> key index
                        frameIndex.set(`${spriteId}_${frame}`, key);

                        // Build PAK -> sprite IDs index
                        const pakSet = pakIndex.get(pakName);
                        if (pakSet) {
                            pakSet.add(spriteId);
                        } else {
                            pakIndex.set(pakName, new Set([spriteId]));
                        }
                    }
                }

                // Yield to main thread between chunks
                loadingStatus.value = `Building indexes... ${Math.min(i + CHUNK_SIZE, totalEntries)}/${totalEntries}`;
                await yieldToMain();
            }

            spritesheetIndex.value = imgIndex;
            spriteIdToImages.value = spriteIndex;
            spriteFrameToKey.value = frameIndex;
            pakToSpriteIds.value = pakIndex;

            console.log(`Atlas loaded: ${totalEntries} tiles, ${loadedAtlas.spritesheets.length} spritesheets, ${imgIndex.size} images, ${pakIndex.size} PAKs indexed`);
            loadingStatus.value = '';
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to load assets';
            console.error('Atlas loading error:', err);
        } finally {
            loading.value = false;
        }
    }

    /**
     * Load a specific spritesheet and create frame textures for it
     */
    async function loadSpritesheet(imagePath: string): Promise<boolean> {
        if (loadedSpritesheets.value.has(imagePath)) return true;
        if (loadingSpritesheets.value.has(imagePath)) {
            // Wait for existing load to complete
            while (loadingSpritesheets.value.has(imagePath)) {
                await new Promise(resolve => setTimeout(resolve, 10));
            }
            return loadedSpritesheets.value.has(imagePath);
        }

        loadingSpritesheets.value.add(imagePath);

        try {
            const fullPath = `assets/${imagePath}`;
            const texture = await Assets.load(fullPath) as PixiTexture;
            baseTextures.value.set(imagePath, texture);

            // Create frame textures using pre-built index
            const entries = spritesheetIndex.value.get(imagePath);
            if (entries) {
                const CHUNK_SIZE = 1000;
                for (let i = 0; i < entries.length; i += CHUNK_SIZE) {
                    const chunk = entries.slice(i, i + CHUNK_SIZE);

                    for (const [key, entry] of chunk) {
                        if (!frameTextures.value.has(key)) {
                            try {
                                const frameTexture = new PixiTexture({
                                    source: texture.source,
                                    frame: new Rectangle(
                                        entry.frame.x,
                                        entry.frame.y,
                                        entry.frame.w,
                                        entry.frame.h
                                    ),
                                });
                                frameTextures.value.set(key, frameTexture);
                            } catch (err) {
                                console.warn(`Failed to create frame: ${key}`, err);
                            }
                        }
                    }

                    // Yield between chunks if there are many frames
                    if (entries.length > CHUNK_SIZE) {
                        await yieldToMain();
                    }
                }
            }

            loadedSpritesheets.value.add(imagePath);
            return true;
        } catch (err) {
            console.warn(`Failed to load spritesheet: ${imagePath}`, err);
            return false;
        } finally {
            loadingSpritesheets.value.delete(imagePath);
        }
    }

    /**
     * Find which spritesheets are needed for given sprite IDs (uses index - O(n) where n = spriteIds)
     */
    function getRequiredSpritesheets(spriteIds: Set<number>): Set<string> {
        const required = new Set<string>();
        for (const spriteId of spriteIds) {
            const images = spriteIdToImages.value.get(spriteId);
            if (images) {
                for (const img of images) {
                    required.add(img);
                }
            }
        }
        return required;
    }

    /**
     * Load all spritesheets required for a set of sprite IDs
     */
    async function loadSpritesForIds(spriteIds: Set<number>, onProgress?: (loaded: number, total: number) => void): Promise<void> {
        // Wait for atlas to be loaded first
        if (!atlas.value) return;

        const required = getRequiredSpritesheets(spriteIds);
        const toLoad = Array.from(required).filter(img => !loadedSpritesheets.value.has(img));

        if (toLoad.length === 0) return;

        console.log(`Loading ${toLoad.length} spritesheets for ${spriteIds.size} sprite IDs...`);
        loading.value = true;
        loadProgress.value = 0;

        try {
            // Load one at a time to avoid overwhelming the browser
            for (let i = 0; i < toLoad.length; i++) {
                loadingStatus.value = `Loading spritesheet ${i + 1}/${toLoad.length}...`;
                await loadSpritesheet(toLoad[i]);
                loadProgress.value = (i + 1) / toLoad.length;
                onProgress?.(i + 1, toLoad.length);
            }

            console.log(`Finished loading ${toLoad.length} spritesheets`);
            loadingStatus.value = '';
        } finally {
            loading.value = false;
        }
    }

    /**
     * Scan a map and load all required sprites
     */
    async function loadSpritesForMap(tiles: Array<{ tileSprite: number; objectSprite: number }>): Promise<void> {
        const spriteIds = new Set<number>();
        for (const tile of tiles) {
            if (tile.tileSprite >= 0) spriteIds.add(tile.tileSprite);
            if (tile.objectSprite > 0) spriteIds.add(tile.objectSprite);
        }
        await loadSpritesForIds(spriteIds);
    }

    /**
     * Get texture for a specific tile sprite and frame (uses index - O(1))
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function getTileTexture(spriteId: number, frame: number): any {
        const key = spriteFrameToKey.value.get(`${spriteId}_${frame}`);
        if (key) {
            return frameTextures.value.get(key);
        }
        return undefined;
    }

    /**
     * Get atlas entry for a specific sprite and frame (uses index - O(1))
     */
    function getAtlasEntry(spriteId: number, frame: number): AtlasEntry | null {
        if (!atlas.value) return null;
        const key = spriteFrameToKey.value.get(`${spriteId}_${frame}`);
        if (key) {
            return atlas.value.tiles[key] ?? null;
        }
        return null;
    }

    /**
     * Get all frames for a sprite ID
     */
    function getFramesForSprite(spriteId: number): number[] {
        const frames: number[] = [];
        // Check the index for all frames of this sprite
        for (const [lookup, _key] of spriteFrameToKey.value) {
            if (lookup.startsWith(`${spriteId}_`)) {
                const frame = parseInt(lookup.split('_')[1], 10);
                frames.push(frame);
            }
        }
        return frames.sort((a, b) => a - b);
    }

    /**
     * Get texture by atlas key directly
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function getTextureByKey(key: string): any {
        return frameTextures.value.get(key);
    }

    /**
     * Get all sprite IDs for a PAK name (O(1) lookup)
     */
    function getSpritesForPak(pakName: string): number[] {
        const sprites = pakToSpriteIds.value.get(pakName.toLowerCase());
        if (sprites) {
            return Array.from(sprites).sort((a, b) => a - b);
        }
        return [];
    }

    /**
     * Get all PAK names
     */
    function getPakNames(): string[] {
        return Array.from(pakToSpriteIds.value.keys()).sort();
    }

    return {
        // State
        atlas,
        baseTextures,
        frameTextures,
        loading,
        error,
        loadProgress,
        loadingStatus,
        loadedSpritesheets,
        // Indexes (for direct access)
        spriteFrameToKey,
        pakToSpriteIds,
        // Computed
        isLoaded,
        availableTileSprites,
        // Actions
        loadAtlas,
        loadSpritesheet,
        loadSpritesForIds,
        loadSpritesForMap,
        getTileTexture,
        getAtlasEntry,
        getFramesForSprite,
        getTextureByKey,
        getSpritesForPak,
        getPakNames,
    };
});
